import { useCallback, useEffect, useState } from "react";
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  Circle,
  ClipboardText,
  MagnifyingGlass,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  getProjectOnboardingStatus,
  refreshProjectOnboarding,
  aiInferDocuments,
} from "../../../api/onboarding";

function RequirementRow({ requirement }) {
  const satisfied = requirement.satisfied;
  return (
    <div className="flex items-center gap-2 py-1.5 px-1">
      {satisfied ? (
        <CheckCircle size={14} weight="fill" className="text-emerald-500 flex-shrink-0" />
      ) : (
        <Circle size={14} className="text-amber-400 flex-shrink-0" />
      )}
      <span className={`text-xs flex-1 ${satisfied ? "text-slate-600" : "text-slate-800 font-medium"}`}>
        {requirement.label}
      </span>
      {satisfied && requirement.satisfied_by && (
        <span className="text-[10px] text-slate-400 truncate max-w-[140px]" title={requirement.satisfied_by}>
          {requirement.satisfied_by.startsWith("field:") ? "✓ on file" : requirement.satisfied_by}
        </span>
      )}
      {!satisfied && requirement.notes && (
        <span className="text-[10px] text-slate-400 truncate max-w-[180px]" title={requirement.notes}>
          {requirement.notes}
        </span>
      )}
    </div>
  );
}

function AiSuggestionRow({ suggestion }) {
  const priorityColors = {
    high: "bg-red-50 text-red-700 border-red-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    low: "bg-slate-50 text-slate-600 border-slate-200",
  };
  const badgeClass = priorityColors[suggestion.priority] || priorityColors.medium;

  return (
    <div className="flex items-start gap-2 py-2 px-1 border-b border-slate-50 last:border-0">
      <MagnifyingGlass size={12} className="text-blue-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-800">{suggestion.document}</span>
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${badgeClass}`}>
            {suggestion.priority}
          </span>
        </div>
        {suggestion.reason && (
          <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{suggestion.reason}</p>
        )}
      </div>
    </div>
  );
}

export function OnboardingStatusSection({ project }) {
  const [expanded, setExpanded] = useState(true);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);

  const projectId = project?.id;

  const loadStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getProjectOnboardingStatus(projectId);
      setStatus(data);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const data = await refreshProjectOnboarding(projectId);
      setStatus(data);
      toast.success("Onboarding status refreshed");
    } catch {
      toast.error("Failed to refresh onboarding status");
    } finally {
      setRefreshing(false);
    }
  };

  const handleAiInfer = async () => {
    setAiLoading(true);
    setAiSuggestions(null);
    try {
      const data = await aiInferDocuments(projectId);
      setAiSuggestions(data.suggestions || []);
      if (!data.suggestions?.length) {
        toast.success("AI analysis complete — no additional documents suggested");
      }
    } catch {
      toast.error("AI analysis failed");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) return null;

  const total = status?.total || 0;
  const met = status?.met || 0;
  const pending = status?.pending || 0;
  const requirements = status?.requirements || [];
  const isAiInferred = status?.mode === "ai_inferred";
  const progressPct = total > 0 ? Math.round((met / total) * 100) : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="onboarding-status-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-700 transition-colors"
        >
          {expanded ? <CaretDown size={10} /> : <CaretRight size={10} />}
          <ClipboardText size={12} className="text-slate-400" />
          Onboarding
          {total > 0 && (
            <span className="ml-2 text-[10px] font-semibold text-slate-400 normal-case tracking-normal">
              {met}/{total}
            </span>
          )}
        </button>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-slate-700 border border-slate-200 hover:border-slate-300 px-2 py-1 rounded transition-colors disabled:opacity-40"
          title="Re-check requirements"
        >
          <ArrowsClockwise size={10} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {expanded && (
        <div className="space-y-3">
          {/* Progress bar */}
          {total > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-slate-500">
                  {met} of {total} requirements met
                </span>
                <span className="text-[10px] font-semibold text-slate-600">{progressPct}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    progressPct === 100 ? "bg-emerald-500" : progressPct >= 50 ? "bg-amber-400" : "bg-amber-300"
                  }`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Requirements list */}
          {requirements.length > 0 && (
            <div className="divide-y divide-slate-50">
              {requirements.map((req) => (
                <RequirementRow key={req.requirement_id} requirement={req} />
              ))}
            </div>
          )}

          {/* AI-inferred mode message */}
          {isAiInferred && requirements.length === 0 && (
            <p className="text-xs text-slate-500 italic py-2">
              No fixed checklist configured. Use the AI Deep Search below to discover what documents might be needed.
            </p>
          )}

          {/* Preset name */}
          {status?.preset_name && !isAiInferred && (
            <p className="text-[10px] text-slate-400">
              Checklist: {status.preset_name}
            </p>
          )}

          {/* AI Deep Search button */}
          <div className="pt-2 border-t border-slate-100">
            <button
              onClick={handleAiInfer}
              disabled={aiLoading}
              className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
            >
              <MagnifyingGlass size={12} weight="bold" />
              {aiLoading ? "Analysing project..." : "AI Deep Search — find other documents that might be needed"}
            </button>
            {aiLoading && (
              <p className="text-[10px] text-slate-400 mt-1 ml-5">
                AI is scanning project files, drawings, and activity...
              </p>
            )}
          </div>

          {/* AI Suggestions */}
          {aiSuggestions !== null && aiSuggestions.length > 0 && (
            <div className="bg-blue-50/50 border border-blue-100 rounded p-3 mt-2">
              <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide mb-2">
                AI Suggestions (beyond checklist)
              </p>
              <div className="space-y-0">
                {aiSuggestions.map((s, i) => (
                  <AiSuggestionRow key={i} suggestion={s} />
                ))}
              </div>
            </div>
          )}

          {aiSuggestions !== null && aiSuggestions.length === 0 && (
            <p className="text-xs text-emerald-600 mt-2">
              AI analysis complete — no additional documents identified as needed.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
